import { setProp } from "@cervval.js/common/src/tools/any.tools";
import { isValidRights, Permission, PermissionRights, PermissionString, Role } from "../dtos.permissions";


/**
 * Give the highest permissions combined by all roles
 * @param allRoles Role[]
 * @returns List of Permission
 */
export function getHighestPermissionsFromRoles(allRoles: Role[]): Permission[] {

    const permissionDict: { [key: string]: Permission } = allRoles.flatMap(ur => ur.permissions).reduce((dict, strPermission) => {
        const permission = deserializePermission(strPermission);
        const key = `${permission.scope}:${permission.name}:${permission.rights}`;
        setProp(dict, key, permission);
        return dict;
    }, {});

    for (const key of Object.keys(permissionDict)) {
        const permission = permissionDict[key];
        if (!permission) {
            continue;
        }
        let lowerRights = <string>permission.rights;
        while (lowerRights.length > 1) {
            lowerRights = lowerRights.slice(0, -1);
            const lowerPermissionKey = `${permission.scope}:${permission.name}:${lowerRights}`;
            const lowerPermission = permissionDict[lowerPermissionKey];
            if (!lowerPermission) {
                continue;
            }
            else if (!permission.includedField && !permission.excludedField) {
                // If the permission is generic we can delete the lower permissions as it includes all fields
                delete permissionDict[lowerPermissionKey];
            }
            else if (permission.includedField && !lowerPermission.excludedField) {
                const lowerIncludedFields = lowerPermission.includedField ?? [];
                lowerPermission.includedField = [...new Set([...lowerIncludedFields, ...permission.includedField])];
            } else if (permission.excludedField && !lowerPermission.includedField && lowerPermission.excludedField) {
                const lowerExcluded = lowerPermission.excludedField;
                const isListEqual = permission.excludedField.length === lowerExcluded.length && permission.excludedField.every((element, index) => element === lowerExcluded[index]);
                if (!isListEqual) {
                    lowerPermission.excludedField = undefined;
                }
            }
        }
    }

    return Object.values(permissionDict);
}


/**
 * Set Permission object as string
 * @param permission Permission
 */
export function serializePermission(permission: Permission): PermissionString {
    let filteredFields = "";
    if (permission.excludedField?.length) {
        filteredFields = `.-${permission.excludedField.join(",")}`;
    } else if (permission.includedField?.length) {
        filteredFields = `.+${permission.includedField.join(",")}`;
    }
    return `${permission.scope}:${permission.name}${filteredFields}:${permission.rights}`;
}

const _permissionMapByPermissionString = new Map<PermissionString, Permission>();
/**
 * Set string permission as Permission object
 */
export function deserializePermissionWithCache(permissionString: PermissionString): Permission {
    let permission = _permissionMapByPermissionString.get(permissionString);
    if (permission == null) {
        permission = deserializePermission(permissionString);
        _permissionMapByPermissionString.set(permissionString, permission);
    }
    return permission;
}

/**
 * Set string permission as Permission object
 * @param permission Permission
 */
export function deserializePermission(permission: PermissionString): Permission {
    const splitPermission = permission.split(":");
    if (splitPermission.length !== 3) {
        return {
            scope: "error",
            name: "bad_permission_format",
            rights: "r"
        };
    }

    let permissionName;
    let includedField;
    let excludedField;

    // Check if the permission is checked on specific field
    const nameAndField = splitPermission[1].split(".");

    if (nameAndField.length === 2 && nameAndField[0] && nameAndField[1]) {
        permissionName = nameAndField[0];
        const strField = nameAndField[1];
        if (strField.startsWith("-")) {
            excludedField = strField.replace("-", "").split(",");
        } else {
            includedField = strField.replace("+", "").split(",");
        }
    } else {
        permissionName = splitPermission[1];
    }

    return {
        scope: splitPermission[0],
        name: permissionName,
        rights: isValidRights(splitPermission[2]) ?
            splitPermission[2] as PermissionRights : "r", // Use read only right if given right is invalid
        includedField,
        excludedField
    };
}
